import React, { useEffect, useRef, useState } from "react";
import RubricMarkdown from "./RubricMarkdown";
import { useAuth } from "../contexts/AuthContext";
import { toPlainMath } from "../utils/plainMath";
import { alignRubricPaperTotal, splitRubricQuestions, replaceRubricBlockAt, replaceRubricBlocks } from "../utils/rubricQuestions";
import { stripQuestionPaperText, restorePaperNotes } from "../utils/questionPaper";
import { chatWithRubricAI } from "../utils/backendApiWrapper";
import { normalizeQuestionId } from "../utils/tableToOneLine";
import "./RubricChatbot.css";

function shownRubric(text) {
  return alignRubricPaperTotal(toPlainMath(text));
}

function normSubject(value) {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function ruleRows(ruleSet) {
  if (!ruleSet) return [];
  if (Array.isArray(ruleSet.rules)) return ruleSet.rules;
  if (typeof ruleSet.rules === "string") {
    try {
      const parsed = JSON.parse(ruleSet.rules);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }
  return [];
}

function ruleSetForRubric(sets, subject) {
  const usable = (Array.isArray(sets) ? sets : []).filter((rs) => ruleRows(rs).length);
  let savedId = "";
  try { savedId = localStorage.getItem("rubricActiveRuleId") || ""; } catch (_) { savedId = ""; }
  const saved = usable.find((rs) => String(rs.id) === String(savedId));
  if (saved) return saved;
  const wanted = normSubject(subject);
  if (!wanted) return null;
  const matches = usable.filter((rs) => {
    const names = [rs.display_name, rs.subject_key, rs.subject].map(normSubject).filter(Boolean);
    return names.some((name) => name === wanted || name.includes(wanted) || wanted.includes(name));
  });
  if (!matches.length) return null;
  return matches.find((rs) => {
    const names = [rs.display_name, rs.subject_key, rs.subject].map(normSubject);
    return names.includes(wanted);
  }) || matches[0];
}

function blockNorm(block) {
  return normalizeQuestionId(block.display) || normalizeQuestionId(`Q.${block.id}`);
}

function isFocusBlock(block, focusKey) {
  const id = blockNorm(block);
  if (!id || !focusKey) return false;
  return id === focusKey || id.startsWith(`${focusKey}.`);
}

export default function RubricQuestionPanel({ content, subject, focusKey, onSave }) {
  const { currentUser } = useAuth();
  const [picked, setPicked] = useState({});
  const [editingIndex, setEditingIndex] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState({ text: "", type: "info" });
  const focusRef = useRef(null);

  const base = shownRubric(content);
  const parts = splitRubricQuestions(base);
  const pickedCount = parts.blocks.filter((b) => picked[b.id]).length;
  const role = currentUser && currentUser.role;

  useEffect(() => {
    const node = focusRef.current;
    if (!node) return;
    node.scrollIntoView({ block: "start" });
  }, [focusKey, content]);

  const saveNext = async (next, successText) => {
    const stored = restorePaperNotes(next, content);
    if (!String(stored || "").trim()) {
      setNote({ text: "Rubric content cannot be empty", type: "error" });
      return false;
    }
    await onSave(stored);
    setNote({ text: successText, type: "success" });
    return true;
  };

  const saveQuestion = async () => {
    if (editingIndex == null || busy) return;
    const original = parts.blocks[editingIndex];
    if (!original) return;
    const replacement = restorePaperNotes(draft, original.text);
    const next = shownRubric(replaceRubricBlockAt(base, editingIndex, replacement));
    setBusy("save");
    try {
      const ok = await saveNext(next, "Question saved. It applies to every student in this folder.");
      if (ok) {
        setEditingIndex(null);
        setDraft("");
      }
    } catch (err) {
      setNote({ text: err.message || "Could not save the question.", type: "error" });
    } finally {
      setBusy("");
    }
  };

  const regenerateSelected = async () => {
    if (busy || editingIndex != null) return;
    const chosen = parts.blocks.filter((b) => picked[b.id]);
    if (!chosen.length) return;
    setBusy("regen");
    setNote({ text: "", type: "info" });
    try {
      const token = localStorage.getItem("authToken");
      const rulesRes = await fetch(`${window.API_BASE_URL}/subject-rules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const rulesData = rulesRes.ok ? await rulesRes.json() : null;
      const ruleSet = ruleSetForRubric(rulesData && rulesData.data, subject);
      const rows = ruleRows(ruleSet);
      if (!ruleSet || !rows.length) {
        setNote({
          text: "Select a Subject Rules set on the rubric generator before regenerating.",
          type: "error"
        });
        return;
      }
      let rubricModel = "";
      let rubricHost = "";
      if (role === "examiner") {
        try {
          rubricModel = localStorage.getItem("examinerRubricModel") || "";
          const host = localStorage.getItem("examinerRubricHost") || "";
          if (host === "gpu" || host === "mac") rubricHost = host;
        } catch (_) { /* server default */ }
      } else if (role === "teacher") {
        const modelRes = await fetch(`${window.API_BASE_URL}/ai/teacher-rubric-model`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (modelRes.ok) {
          const modelData = await modelRes.json();
          rubricModel = modelData.model || "";
          if (String(rubricModel).startsWith("local:") && (modelData.host === "gpu" || modelData.host === "mac")) {
            rubricHost = modelData.host;
          }
        }
      }
      const prompt = `Rewrite ONLY these rubric questions: ${chosen.map((b) => b.display).join(", ")}.
Return one block per listed question and nothing else. Do not include any other question.
Keep each question's label and its "(N marks)" total exactly as they are now.
Write maths in plain text. No LaTeX and no dollar signs. Write 8 ≥ 5 and 4 < 5, not $8 \\ge 5$. Stack an addition or subtraction: the first number, then + or − and the second number, then a line of dashes, then the result. Long division is the bracket stack (12 ) 2345, quotient above, each subtraction on its own line). Never the one-line crossed method with Bring down arrows.

Each block MUST use this shape:

Q.<label> - <question statement> (<N> marks)


Model Answer: <the teacher's answer in plain text>

Rubrics Breakdown:
| Sub-step | Marks |
| --- | --- |
| <step> | <marks> |

The Marks column of each block must add up to that question's "(N marks)".

Current version to replace:
${chosen.map((b) => b.text).join("\n\n")}

Answer key:
${base}
`;
      const reply = await chatWithRubricAI(prompt, "", {
        subjectName: (ruleSet && (ruleSet.subject_key || ruleSet.display_name)) || subject || "",
        subjectRules: rows,
        subjectInstructions: (ruleSet && ruleSet.instructions) || "",
        mode: "rubric",
        rubricModel,
        rubricHost
      });
      const raw = reply && reply.content != null ? reply.content : reply;
      if (typeof raw === "string" && raw.startsWith("Error:")) {
        throw new Error(raw.replace(/^Error:\s*/, "") || "Could not regenerate those questions.");
      }
      const fresh = splitRubricQuestions(shownRubric(raw));
      const map = new Map();
      fresh.blocks.forEach((b) => map.set(b.id, b.text));
      if ([...map.keys()].length === 0 && fresh.blocks.length === chosen.length) {
        chosen.forEach((b, i) => map.set(b.id, fresh.blocks[i].text));
      } else {
        chosen.forEach((b, i) => {
          if (!map.has(b.id) && fresh.blocks.length === chosen.length && fresh.blocks[i]) {
            map.set(b.id, fresh.blocks[i].text);
          }
        });
      }
      if (map.size === 0) throw new Error("The new reply did not include the selected questions.");
      const stillMissing = chosen.filter((b) => !map.has(b.id));
      const next = replaceRubricBlocks(base, map);
      const success = stillMissing.length
        ? `Regenerated ${map.size} question${map.size === 1 ? "" : "s"}. ${stillMissing.map((b) => b.display).join(", ")} stayed as they were.`
        : `Regenerated ${map.size} question${map.size === 1 ? "" : "s"}.`;
      const ok = await saveNext(next, success);
      if (ok) setPicked({});
    } catch (err) {
      setNote({ text: err.message || "Could not regenerate those questions.", type: "error" });
    } finally {
      setBusy("");
    }
  };

  const firstFocus = parts.blocks.findIndex((b) => isFocusBlock(b, focusKey));
  const regenDisabled = !!busy || editingIndex != null || pickedCount === 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12, marginBottom: 8 }}>
        {note.text ? (
          <span style={{ marginRight: "auto", fontSize: 13, color: note.type === "error" ? "#b91c1c" : "#047857" }}>
            {note.text}
          </span>
        ) : (
          <span style={{ marginRight: "auto", fontSize: 13, color: "#6b7280" }}>
            Edits apply to every student in this folder.
          </span>
        )}
        {parts.blocks.length > 0 && (
          <button
            type="button"
            onClick={regenerateSelected}
            disabled={regenDisabled}
            title={editingIndex != null
              ? "Save or cancel the question edit first"
              : pickedCount
                ? `Regenerate ${pickedCount} selected question${pickedCount === 1 ? "" : "s"}`
                : "Select questions to regenerate"}
            style={{
              padding: "6px 12px",
              border: "none",
              borderRadius: 6,
              background: "linear-gradient(135deg, #3B82F6, #2563EB)",
              color: "#fff",
              fontSize: 13,
              fontWeight: 700,
              cursor: regenDisabled ? "not-allowed" : "pointer",
              opacity: regenDisabled ? 0.45 : 1,
              whiteSpace: "nowrap"
            }}
          >
            <i className={busy === "regen" ? "fas fa-spinner fa-spin" : "fas fa-rotate"} style={{ marginRight: 6 }}></i>
            {busy === "regen" ? "Regenerating..." : `Regenerate selected${pickedCount ? ` (${pickedCount})` : ""}`}
          </button>
        )}
      </div>
      <div style={{ flex: 1, overflow: "auto", minHeight: 0, fontSize: 13, lineHeight: 1.6, color: "#1f2937" }}>
        {parts.blocks.length === 0 ? (
          <RubricMarkdown>{base}</RubricMarkdown>
        ) : (
          <>
            {parts.preamble.trim() ? <RubricMarkdown>{parts.preamble}</RubricMarkdown> : null}
            {parts.blocks.map((b, bi) => {
              const editingThis = editingIndex === bi;
              const focused = isFocusBlock(b, focusKey);
              return (
                <div
                  key={`${b.id}-${bi}`}
                  ref={bi === firstFocus ? focusRef : null}
                  className={`rc-qblock${picked[b.id] ? " is-on" : ""}${editingThis ? " is-editing" : ""}`}
                  style={focused ? { boxShadow: "inset 3px 0 0 #2563EB" } : undefined}
                >
                  <label className="rc-qblock__check" title={`Select ${b.display}`}>
                    <input
                      type="checkbox"
                      checked={!!picked[b.id]}
                      disabled={busy || editingThis}
                      onChange={() => setPicked((prev) => ({ ...prev, [b.id]: !prev[b.id] }))}
                      aria-label={`Select ${b.display}`}
                    />
                  </label>
                  <div className="rc-qblock__body">
                    {editingThis ? (
                      <>
                        <textarea
                          className="rc-qedit__box"
                          value={draft}
                          onChange={(e) => setDraft(e.target.value)}
                          autoFocus
                          spellCheck={false}
                          aria-label={`Edit ${b.display}`}
                        />
                        <div className="rc-qedit__actions">
                          <button type="button" className="rc-btn rc-btn--success" onClick={saveQuestion} disabled={busy}>
                            <i className="fas fa-check"></i> Save question
                          </button>
                          <button
                            type="button"
                            className="rc-btn rc-btn--muted"
                            onClick={() => { setEditingIndex(null); setDraft(""); }}
                            disabled={busy}
                          >
                            <i className="fas fa-times"></i> Cancel
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="rc-qblock__tools">
                          <span className="rc-qblock__label">{b.display}</span>
                          <button
                            type="button"
                            className="rc-qedit"
                            disabled={busy}
                            onClick={() => {
                              setEditingIndex(bi);
                              setDraft(stripQuestionPaperText(b.text));
                            }}
                            title={`Edit ${b.display}`}
                          >
                            <i className="fas fa-pen"></i> Edit
                          </button>
                        </div>
                        <RubricMarkdown>{shownRubric(b.text)}</RubricMarkdown>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
